import { useMemo } from 'react'
import { uniqWith } from 'lodash'
import { ChargingSessionModel } from '../../../charging-sessions/models/charging-sessions.model'
import { useFetchWallboxes } from '../../../wallboxes/api/use-fetch-wallboxes'
import { WallboxModel } from '../../../wallboxes/models/wallbox-model'
import { useWallboxLabel } from '../../hooks/use-wallbox-label'
import { wallboxSortFn } from '../../utils/sort-wallboxes'

type WallboxFilterOptionsHook = {
  wallboxOptions: { label: string; value: string }[]
  wallboxes: WallboxModel[]
  isLoadingWallboxes: boolean
}

export const useWallboxFilterOptions = (unfilteredData: ChargingSessionModel[] | undefined, installationId: number): WallboxFilterOptionsHook => {
  const { wallboxes, isLoading } = useFetchWallboxes(installationId)
  const { getWallboxLabel } = useWallboxLabel()

  return useMemo(() => {
    const sessionWallboxes = unfilteredData?.map(session => session.wallbox) || []
    const connectedWallboxes = (wallboxes || []).map(wallbox => ({
      id: wallbox.wallboxId,
      name: wallbox.wallboxName,
      type: wallbox.wallboxType,
    }))

    const availableWallboxes = uniqWith(sessionWallboxes.concat(connectedWallboxes), (wallboxA, wallboxB) => {
      return wallboxA.id === wallboxB.id
    })

    const sortedWallboxes = availableWallboxes
      .map(wallbox => {
        return {
          wallboxName: wallbox.name,
          wallboxId: wallbox.id,
          wallboxType: wallbox.type,
        }
      })
      .sort(wallboxSortFn)

    const wallboxOptions = sortedWallboxes.map(wallbox => ({
      value: wallbox.wallboxId,
      label: getWallboxLabel({ wallboxId: wallbox.wallboxId }),
    }))

    return { wallboxOptions, wallboxes, isLoadingWallboxes: isLoading || !wallboxes }
  }, [isLoading, unfilteredData, wallboxes, getWallboxLabel])
}
